import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../constants/query-keys.contant';
import {
  CashierCloseShiftRequest,
  CashierShiftRequest,
  CashierShiftResponse,
  closeCashierShift,
  getAllActiveCashierShifts,
  getCurrentCashierShift,
  openCashierShift,
} from '../services/cashier.service';
import { useBranchOfficeId } from './use-branch-office-id';

const ONE_MINUTE = 1000 * 60;
const FIVE_MINUTES = 1000 * 60 * 5;

/**
 * Hook to get current shift for a cashier
 */
export const useCurrentCashierShift = (options?: { enabled?: boolean }) => {
  // Key by the active branch so switching branches refetches — a shift is
  // per-branch, and the backend now scopes `/shifts/current` to the
  // X-Branch-Office-Id the FE injects. Without this the cached result from
  // the previous branch leaked across a branch switch.
  const { branchOfficeId } = useBranchOfficeId();
  return useQuery<CashierShiftResponse | null>({
    queryKey: [QueryKeys.CurrentShift, { branchOfficeId }],
    queryFn: () => getCurrentCashierShift(),
    staleTime: ONE_MINUTE,
    gcTime: FIVE_MINUTES,
    enabled: options?.enabled ?? true,
    // 403 CAPABILITY_REQUIRED (business lacks `shifts.enabled`) is
    // NOT a transient error — the caller's fix is to grant the
    // capability, not to retry. TanStack's default `retry: 3` +
    // exponential backoff amplified a bad-perm-state re-render loop
    // into a 30 req/sec storm on `/api/shifts/current`. Fail fast
    // so the guard can settle on an empty shift and render the
    // paywall / gate page.
    retry: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to get all active cashier shifts (for managers)
 */
export const useAllActiveCashierShifts = () => {
  const { branchOfficeId, isBranchSelected } = useBranchOfficeId();

  return useQuery<CashierShiftResponse[]>({
    queryKey: [QueryKeys.ActiveShifts, 'cashier', { branchOfficeId }],
    queryFn: getAllActiveCashierShifts,
    staleTime: ONE_MINUTE,
    gcTime: FIVE_MINUTES,
    refetchInterval: ONE_MINUTE * 2,
    enabled: isBranchSelected,
  });
};

/**
 * Hook to open a new cashier shift
 */
export const useOpenCashierShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CashierShiftRequest) => openCashierShift(data),
    onSuccess: (newShift) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CurrentShift] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ActiveShifts, 'cashier'],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Shifts] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ActiveShifts] });

      // Set the new shift in cache
      queryClient.setQueryData([QueryKeys.CurrentShift], newShift);
    },
  });
};

/**
 * Hook to close a cashier shift
 */
export const useCloseCashierShift = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      shiftId,
      data,
    }: {
      shiftId: string;
      data: CashierCloseShiftRequest;
    }) => closeCashierShift(shiftId, data),
    onSuccess: () => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: [QueryKeys.CurrentShift] });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.ActiveShifts, 'cashier'],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.Shifts] });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ActiveShifts] });

      // Update the shift in cache (will be null since it's closed)
      queryClient.setQueryData([QueryKeys.CurrentShift], null);
    },
  });
};

// Export query keys for external use
export const cashierShiftQueryKeys = {
  all: ['cashier-shifts'],
  current: () => [QueryKeys.CurrentShift] as const,
  active: () => [QueryKeys.ActiveShifts, 'cashier'] as const,
};
